import _ from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import { useRequestHandler } from '@/hooks/useRequestHandler'
import { DiagnosticRTCServiceType, useDiagnosticRTC } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'

import { AudioRecordingData } from '@/components/ui/tables/AudioRecordingTable'
import { useElaraActions } from '@/providers/elara/hooks/useElaraActions'
import { useElaraState } from '@/providers/elara/hooks/useElaraState'
import type { UIAction } from '@/providers/elara/types'

import DiagnosticStethoscope from '../components/DiagnosticStethoscope'
import type { StethoscopeHandle } from '@/components/group/Stethoscope'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectBackgroundTasks, startBackgroundRequestRpiId, stopBackgroundRequestRpiId } from '@/redux/reducers/app.reducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { joinMeeting, leaveMeeting, selectConsultationById, selectRpiMeetingId } from '@/redux/reducers/consultation.reducer'
import {
  addDocument,
  deleteRecordingAnnotation,
  selectDocumentCreateRequestTracker,
  selectRecordingAnnotationDeleteRequestTracker,
  selectRecordingAnnotationUpdateRequestTracker,
  updateRecordingAnnotation,
} from '@/redux/reducers/document.reducer'

export type DiagnosticStethoscopeContainerProps = {
  consultationId?: string
  /** For group consultations: the consultation ID linked to the shared room */
  roomConsultationId?: string
}

export function DiagnosticStethoscopeContainer(props: DiagnosticStethoscopeContainerProps) {
  const { consultationId, roomConsultationId } = props
  const effectiveRoomConsultationId = roomConsultationId || consultationId
  const appDispatch = useAppDispatch()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { activeDiagnosticRTCService, devices, isStreamer, selectDevice: selectRTCDevice } = useDiagnosticRTC()

  const { dispatch } = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectDocumentCreateRequestTracker,
  })

  const deleteRecordingAnnotationRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.recordingAnnotation.delete.success'), { variant: 'success' })
    },
    onError: () => {
      enqueueSnackbar(t('notification.recordingAnnotation.delete.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectRecordingAnnotationDeleteRequestTracker,
  })

  const updateRecordingAnnotationRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.recordingAnnotation.save.success'), { variant: 'success' })
    },
    onError: () => {
      enqueueSnackbar(t('notification.recordingAnnotation.save.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectRecordingAnnotationUpdateRequestTracker,
  })

  const [elaraSelectedDeviceId, setElaraSelectedDeviceId] = useState<string | undefined>()
  const stethoscopeRef = useRef<StethoscopeHandle>(null)

  const meetingType = 'stethoscope'

  const authUser = useAppSelector(selectAuthUser)
  const backgroundTasks = useAppSelector(selectBackgroundTasks)
  const consultation = useAppSelector((state) => (consultationId ? selectConsultationById(state, consultationId) : undefined))
  const rpiMeetingId = useAppSelector(selectRpiMeetingId)

  const audioRecordings = useMemo(
    () =>
      _.map(
        _.filter(consultation?.files, (file) => {
          // backward compatibility for files which start with audio and end with corresponding extension
          // TODO: keep only file.isRecording
          return (file.filename.toLowerCase().startsWith('audio') && file.filename.toLowerCase().endsWith('mp3')) || !!file.isRecording
        }),
        (file) => ({
          key: file.key,
          filename: file.filename,
          url: file.url,
          annotations: file.recordingAnnotations,
        })
      ),
    [consultation]
  )

  const patientInfo = useMemo(
    () => ({
      firstName: consultation?.patient.userInfo.name || '',
      lastName: consultation?.patient.userInfo.surname || '',
    }),
    [consultation]
  )

  useEffect(() => {
    if (isStreamer || !effectiveRoomConsultationId || !!rpiMeetingId) return

    appDispatch(startBackgroundRequestRpiId(effectiveRoomConsultationId))

    return () => {
      appDispatch(stopBackgroundRequestRpiId())
    }
  }, [appDispatch, effectiveRoomConsultationId, isStreamer, rpiMeetingId])

  useEffect(() => {
    if (backgroundTasks.requestRpiId === true && !isStreamer && rpiMeetingId) {
      appDispatch(stopBackgroundRequestRpiId())
    }
  }, [appDispatch, isStreamer, rpiMeetingId, backgroundTasks])

  useEffect(() => {
    if (activeDiagnosticRTCService === DiagnosticRTCServiceType.Chime && authUser && effectiveRoomConsultationId) {
      appDispatch(joinMeeting({ roomOrConsultationId: effectiveRoomConsultationId, userId: authUser._id, meetingType }))
    }
  }, [activeDiagnosticRTCService, appDispatch, authUser, effectiveRoomConsultationId])

  useEffect(() => {
    return () => {
      if (activeDiagnosticRTCService === DiagnosticRTCServiceType.Chime) {
        appDispatch(leaveMeeting(meetingType))
      }
    }
  }, [activeDiagnosticRTCService, appDispatch])

  const handleDeleteAnnotation = (annotationId: string, recordingFile: AudioRecordingData) => {
    if (consultationId) {
      deleteRecordingAnnotationRequestHandler.dispatch(deleteRecordingAnnotation(recordingFile.key, annotationId, 'consultation', consultationId))
    }
  }

  const handleUploadAudioRecording = (file: File) => {
    if (consultationId) dispatch(addDocument({ documentFile: file, isRecording: true, objectType: 'consultation', objectId: consultationId }))
  }

  const handleUpdateAnnotation = (annotation: { id: string; start: number; end: number; note: string }, recordingFile: AudioRecordingData) => {
    if (consultationId) {
      updateRecordingAnnotationRequestHandler.dispatch(updateRecordingAnnotation(recordingFile.key, annotation, 'consultation', consultationId))
    }
  }

  const handleElaraClearDevice = useCallback(() => {
    setElaraSelectedDeviceId(undefined)
  }, [])

  // Resolve the best stethoscope device: prefer identified stethoscopes, fallback to first audio input
  const resolveStethoscopeDeviceId = useCallback((deviceId?: string): string | undefined => {
    if (deviceId) return deviceId
    if (devices.stethoscopes?.length) return devices.stethoscopes[0].device.deviceId
    if (devices.audioInputMediaDevices?.length) return devices.audioInputMediaDevices[0].deviceId
    return undefined
  }, [devices])

  const elaraActions = useMemo((): UIAction[] => [
    {
      actionId: 'stethoscope.selectDevice',
      description: 'Select an audio input device for the stethoscope and start listening. If no deviceId is provided, auto-selects the first available stethoscope device.',
      section: 'stethoscope',
      params: [{ name: 'deviceId', type: 'string' as const, description: 'Device ID of the audio input (optional — auto-selects if omitted)', required: false }],
      handler: async (params) => {
        const deviceId = resolveStethoscopeDeviceId(params?.deviceId)
        if (!deviceId) return { success: false, message: 'No stethoscope or audio input device available' }
        selectRTCDevice(deviceId)
        setElaraSelectedDeviceId(deviceId)
        return { success: true, message: `Stethoscope activated` }
      },
    },
    {
      actionId: 'stethoscope.startRecording',
      description: 'Start recording audio from the stethoscope. Auto-selects device if none is active. Optional durationSeconds for timed recording.',
      section: 'stethoscope',
      params: [{ name: 'durationSeconds', type: 'number' as const, description: 'Auto-stop after N seconds (optional)', required: false }],
      handler: async (params) => {
        if (!stethoscopeRef.current) return { success: false, message: 'Stethoscope not ready' }
        // Auto-select device if not active yet
        if (!elaraSelectedDeviceId) {
          const deviceId = resolveStethoscopeDeviceId()
          if (!deviceId) return { success: false, message: 'No stethoscope or audio input device available' }
          selectRTCDevice(deviceId)
          setElaraSelectedDeviceId(deviceId)
          // Wait briefly for stream to initialize after device selection
          await new Promise((r) => setTimeout(r, 500))
        }
        let started = stethoscopeRef.current.startRecording()
        if (!started) {
          await new Promise((r) => setTimeout(r, 1000))
          started = stethoscopeRef.current.startRecording()
        }
        if (!started) return { success: false, message: 'No audio stream available — select a device first' }
        if (params?.durationSeconds && Number(params.durationSeconds) > 0) {
          const ms = Number(params.durationSeconds) * 1000
          setTimeout(() => { stethoscopeRef.current?.stopRecording() }, ms)
        }
        return { success: true, message: `Recording started${params?.durationSeconds ? ` (${params.durationSeconds}s)` : ''}` }
      },
    },
    {
      actionId: 'stethoscope.stopRecording',
      description: 'Stop the current stethoscope audio recording and save it',
      section: 'stethoscope',
      handler: async () => {
        if (!stethoscopeRef.current) return { success: false, message: 'Stethoscope not ready' }
        stethoscopeRef.current.stopRecording()
        return { success: true, message: 'Recording stopped' }
      },
    },
    {
      actionId: 'stethoscope.stop',
      description: 'Stop the stethoscope completely (done button) — stops recording if active and deselects the device',
      section: 'stethoscope',
      handler: async () => {
        if (!stethoscopeRef.current) return { success: false, message: 'Stethoscope not ready' }
        stethoscopeRef.current.stopDevice()
        setElaraSelectedDeviceId(undefined)
        return { success: true, message: 'Stethoscope stopped' }
      },
    },
  ], [selectRTCDevice, elaraSelectedDeviceId, resolveStethoscopeDeviceId])

  useElaraActions(elaraActions)

  // Report stethoscope state to ELARA so the agent knows current device status
  const stethoscopeState = useMemo(() => ({
    isActive: !!elaraSelectedDeviceId,
    isRecording: !!stethoscopeRef.current?.isRecording,
    selectedDeviceId: elaraSelectedDeviceId || null,
  }), [elaraSelectedDeviceId, stethoscopeRef.current?.isRecording])
  useElaraState('stethoscope', stethoscopeState)

  return (
    <DiagnosticStethoscope
      ref={stethoscopeRef}
      consultationId={consultationId}
      audioRecordings={audioRecordings}
      externalAudioInputActive={!!elaraSelectedDeviceId}
      externalSelectedDeviceId={elaraSelectedDeviceId}
      onRecordStop={handleUploadAudioRecording}
      onDeleteAnnotation={handleDeleteAnnotation}
      onUpdateAnnotation={handleUpdateAnnotation}
      onStopMicro={handleElaraClearDevice}
      patientInfo={patientInfo}
    />
  )
}

export default DiagnosticStethoscopeContainer
